import React, { useMemo, useEffect } from "react";
import BaseTable from "react-bootstrap/Table";
import {
  useTable,
  Column,
  Row,
  ColumnInstance,
  useGlobalFilter,
  useFilters,
  useSortBy,
  usePagination,
} from "react-table";
import { useMappedState } from "redux-react-hook";
import { Spinner } from "react-bootstrap";
import SearchAndFilter, { ColumnFilters } from "../SearchAndFilter";
import TablePagination from "./TablePagination";
import { isPetlabUser } from "../../utilities/petlabUser";

export interface TableProps<T extends object> {
  columns: Column<T>[];
  data: T[];
  hiddenColumns?: string[];
  columnFilterIds?: string[];
  onRowClick?: (row: Row<T>["original"]) => void;
  searchPlaceholder?: string;
  pageCount: number;
  currentPage: number;
  search: string;
  handlePage?: any;
  searchActionName: string;
  sortByForTable?: any;
}

const Table = <T extends object>({
  columns,
  data,
  hiddenColumns = [],
  columnFilterIds,
  onRowClick,
  searchPlaceholder = "Search",
  pageCount,
  currentPage,
  search,
  handlePage,
  searchActionName,
  sortByForTable,
}: React.PropsWithChildren<TableProps<T>>): React.ReactElement<TableProps<T>> => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    state: { pageIndex, sortBy },
    allColumns,
    setGlobalFilter,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageSize: 50,
        hiddenColumns,
      },
      // for server-side pagination
      manualPagination: true,
      manualSortBy: true,
      pageCount,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const showPagination = pageCount > 1 || (isPetlabUser(search) && data.length > 1);
  const isLoading = useMappedState((state): any => state.loading.isLoading);
  const columnFilters = useMemo(() => {
    const filters: ColumnFilters = {};

    allColumns.forEach((col: ColumnInstance<T>) => {
      if (col.Filter) {
        filters[col.id] = col.render("Filter");
      }
    });

    return filters;
  }, [allColumns]);

  useEffect(() => {
    if (sortBy.length > 0) {
      const mappedSortBy = sortBy.map((sort) => {
        if (sort.id === "created_at") return { ...sort, id: "joined" };
        return sort;
      });

      sortByForTable(mappedSortBy);
    }
  }, [sortBy, sortByForTable]);

  return (
    <>
      <SearchAndFilter
        className="mb-3"
        searchPlaceholder={searchPlaceholder}
        setGlobalFilter={setGlobalFilter}
        columnFilters={columnFilters}
        columnFilterIds={columnFilterIds}
        searchActionName={searchActionName}
        search={search}
      />
      <div className="overflow-auto">
        <BaseTable hover {...getTableProps()} className="border-table">
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    className={`${column.canSort ? "sortable " : ""}${column.isSorted
                      ? (column.isSortedDesc ? "sort-desc " : "sort-asc ")
                      : ""
                    }`}
                  >
                    {column.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              if (onRowClick) {
                return (
                  <tr {...row.getRowProps()} onClick={() => onRowClick(row.original)} className="cursor-pointer">
                    {row.cells.map((cell) => (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    ))}
                  </tr>
                );
              }

              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </BaseTable>
      </div>
      {showPagination && (
        <TablePagination
          currentPage={currentPage}
          pageCount={pageCount}
          pageIndex={pageIndex}
          handlePage={handlePage}
        />
      )}
      {isLoading && (
        <div>
          <Spinner as="div" animation="border" size="sm" role="status" />
        </div>
      )}
      {!page.length && !isLoading && search.length !== 0 && (
        <div className="text-center text-danger">
          <h4>No result found</h4>
        </div>
      )}
      {!search.length && !data.length && (
        <div className="text-center min-height-100">
          <h6>This list is no longer loaded on page view. Please search by specific item.</h6>
        </div>
      )}

    </>
  );
};

export default Table;
